import { useQuery } from "@tanstack/react-query";
import { useAuth } from "../context/AuthContext";
import { api } from "../services/api";
import { useCircles } from "./useCircles";

export interface Circle {
  circleId: string;
  name: string;
  description: string;
  role?: string;
  joinedAt?: string;
  alias?: string;
  isPublic: boolean;
  isSubCircle?: boolean;
  parentCircle?: string;
  memberCount?: number;
  createdAt?: string;
  createdBy?: string;
  circleApproved?: boolean;
  logo?: string;
  type?: string;
}

export const useCircleData = () => {
  const { data, isLoading, error, refetch } = useCircles();

  return {
    allCircles: (data || []) as Circle[],
    isLoading,
    error: error ? "Failed to load circles. Please try again." : null,
    refetch,
  };
};

export function userCirclesQueryKey(orgId: string, userId: string) {
  return ["userCircles", orgId, userId] as const;
}

async function fetchUserCircles(
  userId: string,
  orgId: string,
  token: string
): Promise<Circle[]> {
  const response = await api.users.getUserCircles(userId, orgId, token);
  const circles = response.circles ?? [];
  const orgIdUpper = orgId.toUpperCase();
  return circles.filter((circle) => circle.circleId.toUpperCase() !== orgIdUpper);
}

export const useUserCircles = (userId: string, enabled = true) => {
  const { user: authUser, token } = useAuth();
  const orgId = authUser?.schoolId ?? "";

  const query = useQuery({
    queryKey: userCirclesQueryKey(orgId, userId),
    queryFn: () => fetchUserCircles(userId, orgId, token!),
    enabled: Boolean(orgId && token && userId && enabled),
    staleTime: 30_000,
  });

  return {
    userCircles: query.data ?? [],
    isLoading: query.isLoading,
    error: query.isError ? "Failed to load user circles. Please try again." : null,
    refetch: query.refetch,
  };
};

export type UserCirclesQueryResult = ReturnType<typeof useUserCircles>;
